import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalBody } from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getUserLocationHistory } from "../../../helpers/backend";
import dayjs from "dayjs";
import { Map, Layers, Calendar, Navigation, X, Play, Pause, Clock } from "react-feather";
import "./LocationHistoryModal.css";

// Fix Leaflet's default marker icon issue in Webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Custom Icons for markers
const animatedIcon = L.divIcon({
    className: 'custom-circle-icon',
    html: '<div style="background-color: #10b981; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8]
});

const startIcon = L.divIcon({
    className: 'start-icon',
    html: '<div style="background-color: #3b82f6; width: 24px; height: 24px; border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);">A</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
});

const endIcon = L.divIcon({
    className: 'end-icon',
    html: '<div style="background-color: #ef4444; width: 24px; height: 24px; border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 0 5px rgba(0,0,0,0.5);">B</div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
});

const DEFAULT_CENTER = [4.6097, -74.0817];

// Helper to interact with the Map instance
const MapController = ({ history, isPlaying, currentIndex, setMapInstance }) => {
    const map = useMap();

    useEffect(() => {
        setMapInstance(map);
    }, [map, setMapInstance]);

    useEffect(() => {
        if (history.length > 0) {
            const bounds = L.latLngBounds(history.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]));
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [history, map]);

    useEffect(() => {
        if (isPlaying && history[currentIndex]) {
            const point = history[currentIndex];
            map.panTo([parseFloat(point.latitude), parseFloat(point.longitude)], { animate: true });
        }
    }, [isPlaying, currentIndex, history, map]);

    return null;
};

const LocationHistoryModal = ({ isOpen, toggle, user }) => {
    // State
    const [date, setDate] = useState(new Date());
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mapType, setMapType] = useState("roadmap");
    const [mapInstance, setMapInstance] = useState(null);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Animation/Simulation State
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);

    // OSRM Route state
    const [smartRouteCoordinates, setSmartRouteCoordinates] = useState(null);
    const [showSmartRoute, setShowSmartRoute] = useState(true);

    // Reset when modal closes or user changes
    useEffect(() => {
        if (isOpen && user) {
            fetchHistory();
        } else {
            setHistory([]);
            setIsPlaying(false);
            setCurrentIndex(0);
            setSmartRouteCoordinates(null);
            setShowSmartRoute(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user, date]);

    const fetchHistory = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const response = await getUserLocationHistory(user.id, formattedDate);
            const data = Array.isArray(response) ? response : response.data || [];

            const sortedData = data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setHistory(sortedData);

            if (sortedData.length > 0) {
                calculateOSRMRoute(sortedData);
            }
        } catch (error) {
            console.error("Error fetching location history", error);
            setHistory([]);
        } finally {
            setLoading(false);
        }
    };

    // Free routing with OSRM
    const calculateOSRMRoute = async (data = history) => {
        if (data.length < 2) return;
        setLoading(true);

        try {
            // Sampling waypoints up to 25 to respect URL length and API limits
            const maxWaypoints = 23;
            const step = Math.ceil((data.length - 2) / maxWaypoints);
            const waypoints = [data[0]];

            if (data.length > 2) {
                for (let i = 1; i < data.length - 1; i += step) {
                    if (data[i]) waypoints.push(data[i]);
                }
            }
            waypoints.push(data[data.length - 1]);

            const coordsString = waypoints.map(p => `${p.longitude},${p.latitude}`).join(';');

            // Call public OSRM server. Note: This is an open service.
            const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson`);
            const json = await response.json();

            if (json.code === "Ok" && json.routes && json.routes.length > 0) {
                // GeoJSON coordinates are [longitude, latitude], Leaflet needs [latitude, longitude]
                const routeLatLngs = json.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
                setSmartRouteCoordinates(routeLatLngs);
                setShowSmartRoute(true);
            } else {
                console.error("OSRM Route failed", json);
                setShowSmartRoute(false);
            }
        } catch (error) {
            console.error("Error requesting OSRM route:", error);
            setShowSmartRoute(false);
        } finally {
            setLoading(false);
        }
    };

    // Animation Loop
    useEffect(() => {
        if (isPlaying && history.length > 1) {
            const interval = 1000 / playbackSpeed;
            const timer = setInterval(() => {
                setCurrentIndex(prev => {
                    if (prev >= history.length - 1) {
                        setIsPlaying(false);
                        return history.length - 1;
                    }
                    return prev + 1;
                });
            }, interval);
            return () => clearInterval(timer);
        }
    }, [isPlaying, playbackSpeed, history.length]);

    // Calculate Stats
    const stats = useMemo(() => {
        if (history.length < 2) return { distance: 0, duration: 0 };

        let totalDist = 0;
        for (let i = 0; i < history.length - 1; i++) {
            const p1 = history[i];
            const p2 = history[i + 1];
            const R = 6371e3;
            const φ1 = p1.latitude * Math.PI / 180;
            const φ2 = p2.latitude * Math.PI / 180;
            const Δφ = (p2.latitude - p1.latitude) * Math.PI / 180;
            const Δλ = (p2.longitude - p1.longitude) * Math.PI / 180;

            const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            totalDist += R * c;
        }

        const startTime = dayjs(history[0].timestamp);
        const endTime = dayjs(history[history.length - 1].timestamp);
        const duration = endTime.diff(startTime, 'minute');

        return { distance: (totalDist / 1000).toFixed(2), duration: duration, stops: history.length };
    }, [history]);

    // Path for Polyline
    const path = useMemo(() => {
        return history.map(h => [parseFloat(h.latitude), parseFloat(h.longitude)]);
    }, [history]);

    // Current Position for animated Marker
    const currentPosition = useMemo(() => {
        if (!history[currentIndex]) return null;
        return [parseFloat(history[currentIndex].latitude), parseFloat(history[currentIndex].longitude)];
    }, [history, currentIndex]);

    // Map Tiles
    const TILE_URL = mapType === 'roadmap'
        ? "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        : "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

    const TILE_ATTRIBUTION = mapType === 'roadmap'
        ? '&copy; OpenStreetMap contributors'
        : '&copy; Esri &mdash; Portions &copy; OpenStreetMap contributors';

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="modal-location-history" contentClassName="bg-transparent border-0">
            <ModalBody className="p-0 overflow-hidden shadow-lg" style={{ borderRadius: '16px', height: '85vh', background: '#fff' }}>
                <div className="location-history-container">
                    {/* Sidebar */}
                    <div className={`history-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
                        <div className="sidebar-header d-flex justify-content-between align-items-center">
                            <div>
                                <div className="sidebar-title">Historial de Ruta (Leaflet)</div>
                                <div className="sidebar-subtitle">
                                    {user ? `${user.first_name || user.username || 'Usuario'} ${user.last_name || ''}` : "Usuario Desconocido"}
                                </div>
                            </div>
                            <button className="btn btn-sm btn-light text-muted" onClick={toggle}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="sidebar-content">
                            <div className="date-picker-container">
                                <label className="text-muted fw-bold small mb-2 d-block">SELECCIONAR FECHA</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <Calendar size={16} className="text-primary" />
                                    </span>
                                    <Flatpickr
                                        className="form-control border-start-0 ps-0"
                                        value={date}
                                        onChange={([date]) => setDate(date)}
                                        options={{ dateFormat: "Y-m-d", maxDate: "today", disableMobile: true }}
                                    />
                                </div>
                            </div>

                            <div className="row g-2 mb-4">
                                <div className="col-6">
                                    <div className="stat-card d-flex flex-column align-items-center justify-content-center text-center h-100">
                                        <div className="stat-icon primary mb-2">
                                            <Navigation size={16} />
                                        </div>
                                        <div className="stat-value">{stats.distance} <span className="small text-muted fw-normal" style={{ fontSize: '0.8rem' }}>km</span></div>
                                        <div className="stat-label mt-1">Distancia</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="stat-card d-flex flex-column align-items-center justify-content-center text-center h-100">
                                        <div className="stat-icon success mb-2">
                                            <Clock size={16} />
                                        </div>
                                        <div className="stat-value">{stats.duration} <span className="small text-muted fw-normal" style={{ fontSize: '0.8rem' }}>min</span></div>
                                        <div className="stat-label mt-1">Duración</div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="text-muted fw-bold small mb-2 d-block">OPCIONES DE VISTA</label>
                                <div className="d-grid gap-2">
                                    <button
                                        className={`btn btn-sm ${showSmartRoute ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => {
                                            if (!showSmartRoute && !smartRouteCoordinates) {
                                                calculateOSRMRoute();
                                            } else {
                                                setShowSmartRoute(!showSmartRoute);
                                            }
                                        }}
                                    >
                                        <Navigation size={14} className="me-2" />
                                        {showSmartRoute ? "Ocultar Ruta Inteligente" : "Ver Camino (OSRM)"}
                                    </button>
                                </div>
                            </div>

                            <div className="timeline-section">
                                <label className="text-muted fw-bold small mb-3 d-block">REGISTROS ({history.length})</label>
                                {history.length === 0 && !loading && (
                                    <div className="text-center text-muted p-4 bg-light rounded-3">
                                        <Navigation size={24} className="mb-2 opacity-50" />
                                        <p className="mb-0 small">No hay actividad registrada para esta fecha.</p>
                                    </div>
                                )}

                                <div className="timeline-list">
                                    {history.map((point, index) => (
                                        <div
                                            key={index}
                                            className={`timeline-item cursor-pointer ${index === currentIndex ? 'active-point' : ''}`}
                                            onClick={() => {
                                                setCurrentIndex(index);
                                                if (mapInstance) {
                                                    mapInstance.flyTo([parseFloat(point.latitude), parseFloat(point.longitude)], 16);
                                                }
                                            }}
                                        >
                                            <div className={`timeline-dot ${index === currentIndex ? 'bg-success' : ''}`}></div>
                                            <div className="timeline-time pl-2">
                                                {dayjs(point.timestamp).format("HH:mm:ss")}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Map Area */}
                    <div className="map-wrapper" style={{ position: 'relative' }}>
                        {loading && (
                            <div className="loading-overlay">
                                <div className="spinner-border text-primary mb-3" role="status"></div>
                                <div className="fw-bold text-muted">Cargando mapa...</div>
                            </div>
                        )}

                        <div className="map-controls-floating" style={{ zIndex: 1000 }}>
                            <button className={`map-control-btn ${mapType === 'roadmap' ? 'active' : ''}`} onClick={() => setMapType('roadmap')} title="Mapa">
                                <Map size={20} />
                            </button>
                            <button className={`map-control-btn ${mapType === 'satellite' ? 'active' : ''}`} onClick={() => setMapType('satellite')} title="Satélite">
                                <Layers size={20} />
                            </button>
                        </div>

                        {history.length > 0 && (
                            <div className="playback-controls" style={{ zIndex: 1000 }}>
                                <button className="control-btn" onClick={() => setIsPlaying(!isPlaying)}>
                                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                                </button>
                                <div className="slider-container">
                                    <input
                                        type="range"
                                        min="0"
                                        max={history.length - 1}
                                        value={currentIndex}
                                        onChange={(e) => {
                                            setCurrentIndex(parseInt(e.target.value));
                                            setIsPlaying(false);
                                        }}
                                        className="playback-slider"
                                    />
                                </div>
                                <div className="speed-control" onClick={() => setPlaybackSpeed(s => s >= 8 ? 1 : s * 2)}>
                                    {playbackSpeed}x
                                </div>
                                <div className="time-display">
                                    {history[currentIndex] && dayjs(history[currentIndex].timestamp).format("HH:mm:ss")}
                                </div>
                            </div>
                        )}

                        <MapContainer center={DEFAULT_CENTER} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={true}>
                            <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />

                            <MapController
                                history={history}
                                isPlaying={isPlaying}
                                currentIndex={currentIndex}
                                setMapInstance={setMapInstance}
                            />

                            {/* OSRM Smart Route Polyline */}
                            {showSmartRoute && smartRouteCoordinates && (
                                <Polyline positions={smartRouteCoordinates} pathOptions={{ color: '#8b5cf6', weight: 6, opacity: 0.8 }} />
                            )}

                            {/* Standard History Polyline (Raw Data) */}
                            {path.length > 0 && (
                                <Polyline positions={path} pathOptions={{ color: '#3b82f6', weight: 4, opacity: showSmartRoute ? 0.4 : 0.8 }} />
                            )}

                            {/* A and B point markers */}
                            {path.length > 0 && (
                                <>
                                    <Marker position={path[0]} icon={startIcon}>
                                        <Popup>Inicio: {dayjs(history[0].timestamp).format("HH:mm:ss")}</Popup>
                                    </Marker>
                                    <Marker position={path[path.length - 1]} icon={endIcon}>
                                        <Popup>Fin: {dayjs(history[history.length - 1].timestamp).format("HH:mm:ss")}</Popup>
                                    </Marker>
                                </>
                            )}

                            {/* Animated Marker per current Index */}
                            {currentPosition && (
                                <Marker position={currentPosition} icon={animatedIcon} zIndexOffset={100}>
                                    <Popup>Registrado a las {dayjs(history[currentIndex].timestamp).format("HH:mm:ss")}</Popup>
                                </Marker>
                            )}

                        </MapContainer>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default LocationHistoryModal;
